import {
  CERTIFICATION_CHANNEL_NAME,
  CERTIFICATION_MESSAGE_TYPE,
  CertificationMessage,
  CertificationMessageType,
} from '@/shared/types/certification';
import { RestApi } from '@/utils/fetcher';
import { useToast } from 'doctornow-design-system';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';

const usePhoneVerificationViewModel = () => {
  const router = useRouter();
  const { toast } = useToast();
  const { result, EncodeData: responseEncodeData, redirectUrl } = router.query;
  const [requestEncodeData, setRequestEncodeData] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [certificationAlertOpen, setCertificationAlertOpen] = useState(false);
  const processedRef = useRef(false);

  const sendCertificationMessage = useCallback((type: CertificationMessageType) => {
    const message: CertificationMessage = {
      type,
      timestamp: Date.now(),
    };

    if (window.opener && !window.opener.closed) {
      try {
        window.opener.postMessage(message, window.location.origin);
      } catch (error) {
        console.error('window.opener.postMessage failed:', error);
      }
    }

    if ('BroadcastChannel' in window) {
      try {
        const channel = new BroadcastChannel(CERTIFICATION_CHANNEL_NAME);
        channel.postMessage(message);
        channel.close();
      } catch (error) {
        console.error('BroadcastChannel failed:', error);
      }
    }
  }, []);

  const onCloseCertificationPopup = useCallback(() => {
    setCertificationAlertOpen(false);
    if (redirectUrl && typeof redirectUrl === 'string') {
      router.replace(redirectUrl);
    } else {
      window.close();
    }
  }, [router, redirectUrl]);

  const processCertification = useCallback(
    async (requestEncodeData: string) => {
      const config = {
        encodeData: requestEncodeData,
        path: 'auth',
      };

      try {
        await RestApi.post('v1/auth/certifyByPhone', config);
        sendCertificationMessage(CERTIFICATION_MESSAGE_TYPE.SUCCESS);

        if (redirectUrl && typeof redirectUrl === 'string') {
          router.replace(redirectUrl);
        } else {
          window.close();
        }
      } catch (error) {
        sendCertificationMessage(CERTIFICATION_MESSAGE_TYPE.FAIL);

        toast({
          text: error instanceof Error ? error.message : '본인인증에 실패했습니다. 다시 시도해주세요.',
          direction: 'bottom',
        });

        if (!redirectUrl) {
          window.close();
        }
      }
    },
    [router, redirectUrl, toast, sendCertificationMessage]
  );

  const fetchCertificationData = useCallback(async () => {
    try {
      const returnUrlBase = `${process.env.NEXT_PUBLIC_CERTIFICATION_REDIRECT_URI}/phone-verification?result=success`;
      const returnUrl =
        redirectUrl && typeof redirectUrl === 'string'
          ? `${returnUrlBase}&redirectUrl=${encodeURIComponent(redirectUrl)}`
          : returnUrlBase;

      const response = await RestApi.get('v1/auth/certifyByPhone', {
        params: {
          returnUrl: returnUrl,
          errorUrl: `${process.env.NEXT_PUBLIC_CERTIFICATION_REDIRECT_URI}/phone-verification?result=fail`,
        },
      });

      const encodeData = (response.data as { result?: { EncodeData?: string } })?.result?.EncodeData || null;
      setRequestEncodeData(encodeData);
    } catch (error) {
      console.error('Certification data fetch error:', error);
      setCertificationAlertOpen(true);
    } finally {
      setIsLoading(false);
    }
  }, [redirectUrl]);

  const submitForm = useCallback(() => {
    const form = document.getElementById('niceForm') as HTMLFormElement;
    if (form) {
      form.submit();
    }
  }, []);

  useEffect(() => {
    fetchCertificationData();
  }, []);

  useEffect(() => {
    if (result === 'success' && !processedRef.current) {
      processedRef.current = true;
      processCertification(responseEncodeData as string);
      return;
    }

    if (result === 'success' && processedRef.current) {
      return;
    }

    if (result === 'fail') {
      setCertificationAlertOpen(true);
      return;
    }

    if (!requestEncodeData || requestEncodeData === null) {
      if (!isLoading) {
        setCertificationAlertOpen(true);
      }
      return;
    }

    setTimeout(submitForm, 100);
  }, [requestEncodeData, result, responseEncodeData, processCertification, isLoading, submitForm]);

  return {
    requestEncodeData,
    isLoading,
    certificationAlertOpen,
    onCloseCertificationPopup,
  };
};

export default usePhoneVerificationViewModel;
